import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  Eye,
  FileText,
  Pen,
  RefreshCw,
  User,
  UserPlus,
  Users,
  X
} from 'lucide-react';
import {Card, CardContent} from '@/components/ui/card';
import {toast} from '@/hooks/use-toast';
import {useErrorHandler} from '@/hooks/useErrorHandler';
import {
  getAllAppointments,
  getPatients,
  getQueueMy,
  queueCompleteConsultation,
  type QueueEntry,
  type QueueMyResponse,
  queueStartConsultation,
  updateAppointment,
} from '@/lib/api';
import {Button} from '@/components/ui/button';
import {DashboardIcon} from "@radix-ui/react-icons";
import PatientFormModal from '@/components/Patient/PatientFormModal';
import PatientProfileModal from '@/components/Patient/PatientProfileModal';
import AddAppointmentModal from '@/components/Appointment/AddAppointmentModal';
import {useAuth} from '@/context/AuthContext';

// TypeScript interfaces
interface Patient {
  id: string;
  name: string;
  age: number;
  gender: string;
  phone: string;
}

interface Appointment {
  id: string;
  patient_id: string;
  date: string;
  time: string;
  reason: string;
  status: 'scheduled' | 'checked_in' | 'completed' | 'cancelled' | 'no_show' | 'missed' | 'in_progress';
  visit_type?: 'consultation' | 'follow_up' | 'check_up' | 'emergency';
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface AppointmentWithPatientData extends Appointment {
  patientName: string;
  patientAge: number;
  patientGender: string;
  patientPhone: string;
}

interface DashboardStats {
  patientsSeenToday: number;
  prescriptionsWritten: number;
  totalAppointments: number;
  revenue: number;
  completedAppointments: number;
  pendingAppointments: number;
  todayAppointments: number;
}

export default function Home() {
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { currentUser } = useAuth();

  useEffect(() => { document.title = "Dashboard | OneCheckUp"; }, []);

  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Modal states for new features
  const [showNotesModal, setShowNotesModal] = useState<AppointmentWithPatientData | null>(null);
  const [showPatientProfileModal, setShowPatientProfileModal] = useState<AppointmentWithPatientData | null>(null);

  // Patient Form Modal state
  const [showPatientFormModal, setShowPatientFormModal] = useState(false);
  
  // Add Appointment Modal state
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);

  // Doctor queue (waiting + in_consultation)
  const [myQueue, setMyQueue] = useState<QueueMyResponse | null>(null);
  const [queueActionId, setQueueActionId] = useState<string | null>(null);

  // Handle patient form success
  const handlePatientFormSuccess = (newPatient: any, intent: 'patient-only' | 'patient-and-rx') => {
    // Refresh data to include new patient
    loadDashboardData();
    
    if (intent === 'patient-and-rx') {
      // Navigate to prescription page for the new patient
      navigate('/prescriptions/new', {
        state: {
          patient: newPatient,
          from: 'dashboard'
        }
      });
    }
  };

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      if (!currentUser?.doctor_id || !currentUser?.clinic_id) {
        setPatients([]);
        setAppointments([]);
        setMyQueue(null);
        return;
      }
      const [appointmentsData, patientsData, queueData] = await Promise.all([
        getAllAppointments(currentUser.tenant_id, currentUser.clinic_id),
        getPatients({
          tenant_id: currentUser.tenant_id,
          clinic_id: currentUser.clinic_id,
        }),
        currentUser?.role === 'doctor' ? getQueueMy() : Promise.resolve(null)
      ]);
      if (appointmentsData) setAppointments(appointmentsData);
      if (patientsData && Array.isArray(patientsData.patients)) setPatients(patientsData.patients);
      if (queueData) setMyQueue(queueData);
      else setMyQueue(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      handleError(err, 'Dashboard Loading');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  // Helper functions
  const getPatientDetails = (patientId: string): Patient => {
    return patients.find(p => p.id === patientId) || {
      id: '',
      name: 'Unknown Patient',
      age: 0,
      gender: 'Unknown',
      phone: 'N/A'
    };
  };

  // Visit type utilities
  const getVisitTypeLabel = (visitType?: string) => {
    const types = {
      'consultation': 'Consultation',
      'follow_up': 'Follow-up',
      'check_up': 'Check-up',
      'emergency': 'Emergency'
    };
    return types[visitType as keyof typeof types] || 'General Visit';
  };

  const getVisitTypeColor = (visitType?: string) => {
    const colors = {
      'consultation': 'text-primary bg-primary/10',
      'follow_up': 'text-warning bg-warning/10',
      'check_up': 'text-success bg-success/10',
      'emergency': 'text-destructive bg-destructive/10'
    };
    return colors[visitType as keyof typeof colors] || 'text-muted-foreground bg-muted/10';
  };

  const parseAppointmentData = (appointment: Appointment): AppointmentWithPatientData => {
    const patient = getPatientDetails(appointment.patient_id);
    return {
      ...appointment,
      patientName: patient.name,
      patientAge: patient.age,
      patientGender: patient.gender,
      patientPhone: patient.phone
    };
  };

  const formatTime = (timeString: string) => {
    if (!timeString) return 'Not specified';
    try {
      // Handle both HH:MM and HH:MM:SS formats
      const timeParts = timeString.split(':');
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);

      // Create a proper time for comparison
      const date = new Date();
      date.setHours(hours, minutes, 0, 0);

      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      console.error('Error formatting time:', error, timeString);
      return timeString;
    }
  };

  const isToday = (dateString: string | Date) => {
    try {
      const appointmentDate = new Date(dateString);
      const today = new Date();
      return appointmentDate.toDateString() === today.toDateString();
    } catch (error) {
      console.error('Error checking if date is today:', error, dateString);
      return false;
    }
  };

  // Improved time comparison for appointment status
  const getAppointmentStatus = (appointment: Appointment) => {
    try {
      const now = new Date();
      const appointmentDate = new Date(appointment.date);

      // Set the appointment time
      const timeParts = appointment.time.split(':');
      appointmentDate.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);

      const timeDiff = appointmentDate.getTime() - now.getTime();
      const isToday = appointmentDate.toDateString() === now.toDateString();

      return {
        isToday,
        isPast: timeDiff < 0 && isToday,
        isUpcoming: timeDiff > 0 && isToday,
        appointmentTime: appointmentDate
      };
    } catch (error) {
      console.error('Error getting appointment status:', error);
      return {
        isToday: false,
        isPast: false,
        isUpcoming: false,
        appointmentTime: new Date()
      };
    }
  };

  // Calculate dashboard statistics
  const stats: DashboardStats = useMemo(() => {
    const todayAppointments = appointments.filter(apt => isToday(apt.date));
    const completedToday = todayAppointments.filter(apt => apt.status === 'completed');

    return {
      patientsSeenToday: completedToday.length,
      prescriptionsWritten: completedToday.length * 1.2, // Estimate
      totalAppointments: todayAppointments.length,
      revenue: completedToday.length * 150, // Estimate $150 per consultation
      completedAppointments: completedToday.length,
      pendingAppointments: todayAppointments.filter(apt => apt.status === 'scheduled').length,
      todayAppointments: todayAppointments.length
    };
  }, [appointments]);

  // Get today's appointments with patient data and improved sorting
  const todayAppointmentsWithStatus = useMemo(() => {
    return appointments
    .filter(apt => isToday(apt.date))
    .map(parseAppointmentData)
    .map(apt => {
      const status = getAppointmentStatus(apt);
      return { ...apt, ...status };
    })
    .sort((a, b) => a.appointmentTime.getTime() - b.appointmentTime.getTime());
  }, [appointments, patients]);

  // Get next appointment with improved logic
  const nextAppointment = useMemo(() => {
    const now = new Date();

    const upcoming = todayAppointmentsWithStatus
    .filter(apt => apt.status === 'scheduled' && apt.isUpcoming)
    .sort((a, b) => a.appointmentTime.getTime() - b.appointmentTime.getTime());

    return upcoming[0] || null;
  }, [todayAppointmentsWithStatus]);

  // Enhanced professional navigation handlers with validation and feedback
  const handleStartConsultation = async (appointment: AppointmentWithPatientData) => {
    try {
      // Validation checks
      if (!appointment?.patient_id || !appointment?.id) {
        toast({
          title: "Navigation Error",
          description: "Invalid appointment data. Please refresh and try again.",
          variant: "destructive"
        });
        return;
      }

      // Professional feedback
        toast({
        title: "Starting Consultation",
        description: `Opening consultation for ${appointment.patientName}`,
        variant: "default",
        duration: 1000
      });

      // Navigate with proper error handling
      navigate(`/patients/${appointment.patient_id}?openPrescription=true`);
    } catch (error) {
      console.error('Error starting consultation:', error);
      toast({
        title: "Navigation Failed",
        description: "Unable to start consultation. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Enhanced routing with validation
  const handleQuickAction = async (action: string) => {
    try {
      // Professional user feedback for navigation actions
      const actionMessages = {
        'write-prescription': 'Opening prescription writer...',
        'add-appointment': 'Opening appointment scheduler...',
        'create-template': 'Opening template creator...'
      };

      const message = actionMessages[action as keyof typeof actionMessages];
      if (message) {
      toast({
          title: "Navigation",
          description: message,
          variant: "default"
        });
      }

      // Enhanced routing with validation
      switch (action) {
        case 'write-prescription':
          navigate('/prescriptions', { state: { mode: 'create' } });
          break;
        case 'add-patient':
          setShowPatientFormModal(true);
          break;
        case 'add-appointment':
          setShowAddAppointmentModal(true);
          break;
        case 'create-template':
          navigate('/templates', { state: { mode: 'create' } });
          break;
        default:
          console.warn('Unknown quick action:', action);
        toast({
            title: "Invalid Action",
            description: "This feature is not available yet.",
            variant: "destructive"
          });
          break;
      }
    } catch (error) {
      console.error('Error in quick action navigation:', error);
        toast({
        title: "Navigation Error",
        description: "Unable to complete the action. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Professional navigation with context awareness
  const handleViewAppointments = () => {
    try {
      // Add analytics or tracking here if needed
      console.log('User navigating to appointments from dashboard');

      toast({
        title: "Navigation",
        description: "Opening appointments management...",
        variant: "default"
      });

      navigate('/appointments', {
        state: {
          from: 'dashboard',
          defaultFilter: 'today'
        }
      });
    } catch (error) {
      console.error('Error navigating to appointments:', error);
      toast({
        title: "Navigation Error",
        description: "Unable to open appointments. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Enhanced appointment action handlers
  const handleAppointmentAction = async (appointment: AppointmentWithPatientData, action: 'start' | 'view' | 'manage' | 'notes' | 'profile' | 'reschedule' | 'progress') => {
    try {
      // Validation
      if (!appointment?.id) {
        toast({
          title: "Error",
          description: "Invalid appointment data.",
          variant: "destructive"
        });
        return;
      }

      switch (action) {
        case 'start':
          // Update status to in_progress and start consultation (appointment list path)
          await updateAppointmentStatus(appointment, 'in_progress');
          await handleStartConsultation(appointment);
          break;
        case 'view':
          toast({
            title: "Navigation",
            description: `Opening prescriptions for ${appointment.patientName}`,
            variant: "default"
          });
          navigate(`/prescriptions`, {
            state: {
              patientId: appointment.patient_id,
              patientName: appointment.patientName,
              filter: 'patient'
            }
          });
          break;
        case 'manage':
          toast({
            title: "Navigation",
            description: "Opening appointment management...",
            variant: "default"
          });
          navigate('/appointments', {
            state: {
              selectedAppointment: appointment.id,
              focus: true
            }
          });
          break;
        case 'notes':
          setShowNotesModal(appointment);
          break;
        case 'profile':
          setShowPatientProfileModal(appointment);
          break;
        case 'progress':
          await updateAppointmentStatus(appointment, 'completed');
          toast({
            title: "Consultation Completed",
            description: `Consultation for ${appointment.patientName} has been marked as completed`,
            variant: "default"
          });
          break;
        default:
          console.warn('Unknown appointment action:', action);
      }
    } catch (error) {
      console.error('Error in appointment action:', error);
      toast({
        title: "Action Failed",
        description: "Unable to complete the action. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Helper function to update appointment status
  const updateAppointmentStatus = async (appointment: AppointmentWithPatientData, newStatus: string) => {
    try {
      // Update local state immediately for better UX
      setAppointments(prev =>
          prev.map(apt =>
              apt.id === appointment.id
                  ? { ...apt, status: newStatus as any, updated_at: new Date().toISOString() }
                  : apt
          )
      );

      // Call API to update the backend
      await updateAppointment({
        id: appointment.id,
        status: newStatus,
        updated_at: new Date().toISOString()
      });

    } catch (error) {
      console.error('Error updating appointment status:', error);
      // Revert local state on error
      await loadDashboardData();
    }
  };

  // Handle reschedule completion
  const handleRescheduleCompleted = (updatedAppointment: any) => {
    // Update local state with the rescheduled appointment
    setAppointments(prev =>
      prev.map(apt =>
        apt.id === updatedAppointment.id
          ? { ...apt, ...updatedAppointment, updated_at: new Date().toISOString() }
          : apt
      )
    );
  };

  // Doctor queue: call next (start consultation)
  const handleQueueStart = async (entry: QueueEntry) => {
    if (!entry.patientId) {
      toast({ title: "Error", description: "No patient linked to this queue entry.", variant: "destructive" });
      return;
    }
    try {
      setQueueActionId(entry.id);
      await queueStartConsultation(entry.id);
      toast({ title: "Consultation started", description: `Calling ${entry.patientName} (Token #${entry.tokenNumber})` });
      await loadDashboardData();
      navigate(`/patients/${entry.patientId}?openPrescription=true`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to start consultation";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setQueueActionId(null);
    }
  };

  // Doctor queue: complete consultation
  const handleQueueComplete = async (entry: QueueEntry) => {
    try {
      setQueueActionId(entry.id);
      await queueCompleteConsultation(entry.id);
      toast({ title: "Consultation completed", description: `${entry.patientName} — Token #${entry.tokenNumber}` });
      await loadDashboardData();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to complete consultation";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setQueueActionId(null);
    }
  };

  // Smart navigation for KPI cards (professional feature)
  const handleKPICardClick = (cardType: string) => {
    try {
      const navigationMap = {
        'patients': '/appointments?filter=completed',
        'prescriptions': '/prescriptions?filter=today',
        'appointments': '/appointments?filter=today',
        'revenue': '/billing?filter=today'
      };

      const route = navigationMap[cardType as keyof typeof navigationMap];
      if (route) {
        navigate(route, { state: { from: 'dashboard-kpi' } });
      }
    } catch (error) {
      console.error('Error in KPI navigation:', error);
    }
  };

  const calculateWaitingTime = (entry: QueueEntry) => {
    if (entry.waitingTimeMinutes === undefined) return 'Waiting';
    else if (entry.waitingTimeMinutes < 5) return 'Just arrived';
    else if (entry.waitingTimeMinutes < 60) return 'Waiting ·' + `${entry.waitingTimeMinutes} mins`;
    else {
      const hours = Math.floor(entry.waitingTimeMinutes / 60);
      const minutes = entry.waitingTimeMinutes % 60;
      return 'Waiting ·' +`${hours} hours ${minutes} mins`;
    }
  }

  if (loading) {
    return (
        <div className="prescription-form-container">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="prescription-form-container">
        {/* Welcome Header - Using layout's padding */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary rounded-lg">
              <DashboardIcon className="w-5 h-5 text-primary-foreground"/>
            </div>
        <div>
              <h3 className="text-m font-bold text-foreground">
                Dashboard
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Manage your appointments, patients, and more
          </p>
        </div>
      </div>

          <div className="flex items-center gap-2">
            <Button
                onClick={handleRefresh}
                disabled={refreshing}
                className="medical-button-secondary p-2.5 border-border hover:bg-accent"
                title="Refresh Dashboard"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`}/>
            </Button>
            <button
              onClick={() => handleQuickAction('write-prescription')}
              className="medical-button-secondary p-2.5 border-border hover:bg-accent"
              title="Write Prescription"
            >
              <Pen className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleQuickAction('add-patient')}
              className="medical-button-secondary p-2.5 border-border hover:bg-accent"
              title="Add Patient"
            >
              <UserPlus className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleQuickAction('add-appointment')}
              className="medical-button-secondary p-2.5 border-border hover:bg-accent"
              title="Add Appointment"
            >
              <Calendar className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleQuickAction('create-template')}
              className="medical-button-secondary p-2.5 border-border hover:bg-accent"
              title="Create Template"
            >
              <ClipboardList className="w-4 h-4" />
            </button>
            <Button
                onClick={handleViewAppointments}
                className="medical-button-primary flex items-center gap-2 px-4 py-2.5"
            >
              <Calendar className="w-4 h-4"/>
              <span>All Appointments</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-6">
          {/* Left: Queue Panel — doctor only */}
          {currentUser?.role === 'doctor' && myQueue && (() => {
            const inConsultation = myQueue.entries.find(e => e.status === 'in_consultation');
            const waitingEntries = myQueue.entries.filter(e => e.status === 'waiting');
            const nextUp = waitingEntries[0];
            const remainingWaiting = waitingEntries.slice(1);
            return (
              <div className="lg:col-span-3">
                <Card className="medical-card">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-m font-semibold flex items-center gap-2">
                        <Users className="w-5 h-5 text-warning" />
                        My Queue
                      </h4>
                      <span className="text-xs text-muted-foreground">
                        {myQueue.summary.totalWaiting} waiting · {myQueue.summary.totalInConsultation} in consultation
                      </span>
                    </div>

                    {!inConsultation && waitingEntries.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-12 text-center">
                        <Users className="w-10 h-10 text-muted-foreground/30 mb-3" />
                        <p className="text-sm font-medium text-muted-foreground">Queue is empty</p>
                        <p className="text-xs text-muted-foreground/60 mt-1">
                          Front Desk will add patients here
                        </p>
                      </div>
                    ) : (
                      <>
                    {/* Zone 1: In Consultation */}
                    {inConsultation ? (
                      <div className="ring-2 ring-primary/30 bg-primary/5 rounded-xl p-4 mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Now In Consultation</p>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center">
                            <span className="text-sm font-bold text-warning">{inConsultation.tokenNumber}</span>
                          </div>
                          <div>
                            <p className="font-bold text-lg text-foreground">{inConsultation.patientName}</p>
                            <p className="text-xs text-muted-foreground">In Consultation</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => navigate(`/patients/${inConsultation.patientId}?openPrescription=true`)}
                            className="flex-1 medical-button-secondary text-sm py-2"
                          >
                            Open Prescription
                          </button>
                          <button
                            onClick={() => handleQueueComplete(inConsultation)}
                            disabled={queueActionId !== null}
                            className="flex-1 medical-button-primary text-sm py-2 bg-success hover:bg-success/90"
                          >
                            {queueActionId === inConsultation.id ? 'Completing…' : 'Complete →'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-muted/20 rounded-xl p-4 mb-4 text-center">
                        <p className="text-sm text-muted-foreground">No active consultation</p>
                      </div>
                    )}

                    {/* Zone 2: Next Up */}
                    {nextUp ? (
                      <div className="bg-muted/30 rounded-xl p-4 mb-4">
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Next Up</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-warning/20 flex items-center justify-center">
                              <span className="text-sm font-bold text-warning">{nextUp.tokenNumber}</span>
                            </div>
                            <div>
                              <p className="font-semibold text-foreground">{nextUp.patientName}</p>
                              <p className="text-xs text-muted-foreground">
                                { calculateWaitingTime(nextUp) }
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleQueueStart(nextUp)}
                            disabled={queueActionId !== null}
                            className="medical-button-primary text-sm px-4 py-2"
                          >
                            {queueActionId === nextUp.id ? 'Starting…' : 'Call Next'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-muted/10 rounded-xl p-4 mb-4 flex items-center justify-center min-h-[80px]">
                        <p className="text-sm text-muted-foreground">No patients waiting</p>
                      </div>
                    )}

                    {/* Zone 3: Waiting List */}
                    {remainingWaiting.length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Waiting</p>
                        <div className="space-y-2">
                          {remainingWaiting.map(entry => (
                            <div key={entry.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/20">
                              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                                <span className="text-xs font-bold text-muted-foreground">{entry.tokenNumber}</span>
                              </div>
                              <p className="text-sm text-foreground flex-1">{entry.patientName}</p>
                              {entry.waitingTimeMinutes != null && (
                                <span className="text-xs text-muted-foreground">{ calculateWaitingTime(entry) }</span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                      </>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Right: Today's Appointments */}
          <div className={currentUser?.role === 'doctor' && myQueue ? 'lg:col-span-2' : 'lg:col-span-5'}>
            <Card className="medical-card">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="test-m font-semibold text-foreground flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-primary"/>
                    Today's Appointments ({todayAppointmentsWithStatus.length})
                  </h4>
                  <button
                      onClick={handleViewAppointments}
                      className="medical-button-secondary text-sm"
                  >
                  View All
                  </button>
              </div>

                {todayAppointmentsWithStatus.length > 0 ? (
                    <div className="space-y-3">
                      {todayAppointmentsWithStatus.map((appointment) => {
                        const {isToday, isPast, isUpcoming} = appointment;

                        return (
                            <div
                                key={appointment.id}
                                data-appointment-id={appointment.id}
                                className={`medical-section p-3 transition-all hover:shadow-md ${
                                    isUpcoming && appointment.status === 'scheduled' ? 'ring-2 ring-warning/20 bg-warning/5' : ''
                                } ${
                                    appointment.status === 'completed' ? 'bg-success/5 border-success/20' : ''
                                } ${
                                    isPast && appointment.status === 'scheduled' ? 'bg-destructive/5 border-destructive/20' : ''
                                } ${
                                    appointment.status === 'in_progress' ? 'ring-2 ring-primary/30 bg-primary/5' : ''
                                }`}
                            >
                              <div className="flex items-center gap-2">
                                <div className="text-center min-w-[70px]">
                                  <div className="text-sm font-bold text-foreground">
                                    {formatTime(appointment.time)}
                                  </div>
                                </div>

                                <div className="flex items-center justify-between w-full gap-2">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                                            appointment.status === 'completed' ? 'bg-success/10' :
                                                appointment.status === 'in_progress' ? 'bg-primary/10' :
                                                    isUpcoming && appointment.status === 'scheduled' ? 'bg-warning/10' :
                                                        'bg-primary/10'
                                        }`}>
                                      <User className={`w-5 h-5 ${
                                          appointment.status === 'completed' ? 'text-success' :
                                              appointment.status === 'in_progress' ? 'text-primary' :
                                                  isUpcoming && appointment.status === 'scheduled' ? 'text-warning' :
                                                      'text-primary'
                                      }`}/>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-foreground whitespace-nowrap overflow-hidden text-ellipsis">{appointment.patientName}</h4>
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <span>
                                          {appointment.patientAge}y • {appointment.patientGender}
                                          {appointment.reason && ` • ${appointment.reason.substring(0, 30)}${appointment.reason.length > 30 ? '...' : ''}`}
                                        </span>
                                        {appointment.visit_type && (
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getVisitTypeColor(appointment.visit_type)}`}>
                                              {getVisitTypeLabel(appointment.visit_type)}
                                            </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1 flex-shrink-0">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                        appointment.status === 'completed' ? 'bg-success/10 text-success' :
                                            appointment.status === 'cancelled' ? 'bg-destructive/10 text-destructive' :
                                                appointment.status === 'in_progress' ? 'bg-primary/10 text-primary' :
                                                    isPast && appointment.status === 'scheduled' ? 'bg-destructive/10 text-destructive' :
                                                        isUpcoming && appointment.status === 'scheduled' ? 'bg-warning/10 text-warning' :
                                                            'bg-primary/10 text-primary'
                                    }`}>
                                      {appointment.status === 'in_progress' ? 'In Progress' :
                                          appointment.status === 'scheduled' && isPast ? 'Missed' :
                                              appointment.status.charAt(0).toUpperCase() + appointment.status.slice(1)}
                                    </span>

                                    {appointment.status === 'scheduled' && !isPast ? (
                                        <button
                                            onClick={() => handleAppointmentAction(appointment, 'start')}
                                            className="medical-button-primary px-4 py-2 text-sm whitespace-nowrap"
                                        >
                                          Start
                                        </button>
                                    ) : appointment.status === 'in_progress' ? (
                                        <button
                                            onClick={() => handleAppointmentAction(appointment, 'progress')}
                                            className="medical-button-primary px-3 py-1.5 text-sm bg-success hover:bg-success/90 whitespace-nowrap"
                                        >
                                          Complete
                                        </button>
                                    ) : appointment.status === 'completed' ? (
                                        <button
                                            onClick={() => handleAppointmentAction(appointment, 'notes')}
                                            className="medical-button-secondary px-3 py-1.5 text-sm whitespace-nowrap flex items-center gap-1"
                                        >
                                          <FileText className="w-4 h-4"/>
                                          Notes
                                        </button>
                                    ) : isPast && appointment.status === 'scheduled' ? (
                                        <button
                                            onClick={() => handleAppointmentAction(appointment, 'start')}
                                            className="medical-button-primary px-3 py-1.5 text-sm whitespace-nowrap"
                                        >
                                          Start
                                        </button>
                                    ) : null}

                                    {appointment.status !== 'cancelled' && (
                                        <button
                                            onClick={() => handleAppointmentAction(appointment, 'profile')}
                                            className="p-2 text-muted-foreground hover:text-info hover:bg-info/10 rounded-lg transition-colors"
                                            title="View Patient Profile"
                                        >
                                          <Eye className="w-4 h-4"/>
                                        </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                        );
                      })}
                    </div>
                ) : (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 text-success mx-auto mb-3"/>
                      <p className="text-muted-foreground mb-2">No appointments today</p>
                      <p className="text-sm text-muted-foreground">Schedule appointments to see them here</p>
                    </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* KPI Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          <Card
              className="medical-card compact stats-card cursor-pointer hover:shadow-lg transition-all duration-200"
              onClick={() => handleKPICardClick('patients')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPICardClick('patients');
                }
              }}
              aria-label="View patients seen today details"
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Patients Seen Today</p>
                  <p className="text-2xl font-bold text-success">{stats.patientsSeenToday}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    +{stats.patientsSeenToday > 0 ? Math.round((stats.patientsSeenToday / stats.todayAppointments) * 100) : 0}% completion rate
                  </p>
                </div>
                <div className="w-8 h-8 bg-success/10 rounded-lg flex items-center justify-center group-hover:bg-success/20 transition-colors">
                  <Users className="w-4 h-4 text-success"/>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
              className="medical-card compact stats-card cursor-pointer hover:shadow-lg transition-all duration-200"
              onClick={() => handleKPICardClick('prescriptions')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPICardClick('prescriptions');
                }
              }}
              aria-label="View prescriptions written today details"
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Prescriptions Written</p>
                  <p className="text-2xl font-bold text-primary">{Math.floor(stats.prescriptionsWritten)}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Avg {stats.patientsSeenToday > 0 ? (stats.prescriptionsWritten / stats.patientsSeenToday).toFixed(1) : 0} per patient
                  </p>
                </div>
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <FileText className="w-4 h-4 text-primary"/>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card
              className="medical-card compact stats-card cursor-pointer hover:shadow-lg transition-all duration-200"
              onClick={() => handleKPICardClick('appointments')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleKPICardClick('appointments');
                }
              }}
              aria-label="View total appointments today details"
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Total Appointments</p>
                  <p className="text-2xl font-bold text-warning">{stats.totalAppointments}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {stats.pendingAppointments} pending today
                  </p>
                </div>
                <div className="w-8 h-8 bg-warning/10 rounded-lg flex items-center justify-center group-hover:bg-warning/20 transition-colors">
                  <Calendar className="w-4 h-4 text-warning"/>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="medical-card compact stats-card transition-all duration-200">
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">Avg Consult Time</p>
                  <p className="text-2xl font-bold text-muted-foreground">--</p>
                  <p className="text-xs text-muted-foreground mt-1">mins per patient</p>
                </div>
                <div className="w-8 h-8 bg-muted/20 rounded-lg flex items-center justify-center">
                  <Clock className="w-4 h-4 text-muted-foreground"/>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Modal Components with consistent medical theme spacing... */}
        {showNotesModal && (
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                              <div className="medical-card w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl mx-4">
                  <div
                      className="modal-header-professional bg-gradient-to-r from-info/5 to-info/10 border-b border-border">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div
                          className="w-10 h-10 bg-info/10 rounded-full flex items-center justify-center">
                        <FileText className="w-5 h-5 text-info"/>
                      </div>
                  <div>
                        <h3 className="text-m font-semibold text-foreground">Visit Notes</h3>
                        <p className="text-sm text-muted-foreground">{showNotesModal.patientName}</p>
                      </div>
                    </div>
                    <button
                        onClick={() => setShowNotesModal(null)}
                        className="p-2 hover:bg-muted/80 rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <X className="w-5 h-5"/>
                    </button>
                  </div>
                </div>

                <div className="p-4 max-h-[calc(90vh-8rem)] overflow-y-auto">
                  <div className="space-y-4">
                    <div className="medical-section p-3">
                      <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                        <FileText className="w-5 h-5 text-info"/>
                        Current Visit Notes
                      </h4>
                      <div className="bg-muted/30 p-3 rounded-lg">
                        <p className="text-foreground">
                          {showNotesModal.notes || 'No notes available for this visit.'}
                    </p>
                  </div>
                </div>

                    <div className="medical-section p-3">
                      <h4 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Clock className="w-5 h-5 text-primary"/>
                        Recent Visit History
                      </h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-muted/20 rounded">
                          <span className="text-sm font-medium text-foreground">Last visit: 3 days ago</span>
                          <span className="text-xs text-success">Completed</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-muted/20 rounded">
                          <span className="text-sm font-medium text-foreground">Follow-up: 1 week ago</span>
                          <span className="text-xs text-success">Completed</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 p-4 border-t border-border">
                  <button
                      onClick={() => setShowNotesModal(null)}
                      className="medical-button-secondary"
                  >
                    Close
                  </button>
                  <button
                      onClick={() => {
                        // Handle edit notes
                        setShowNotesModal(null);
                      }}
                      className="medical-button-primary"
                  >
                    Edit Notes
                  </button>
              </div>
                </div>
            </div>
        )}

        {/* Patient Profile Modal */}
        <PatientProfileModal
          patient={showPatientProfileModal}
          isOpen={!!showPatientProfileModal}
          onClose={() => setShowPatientProfileModal(null)}
          onStartConsultation={(appointment) => handleAppointmentAction(appointment, 'start')}
        />

        {/* Patient Form Modal */}
        <PatientFormModal
          isOpen={showPatientFormModal}
          onClose={() => setShowPatientFormModal(false)}
          onSuccess={handlePatientFormSuccess}
          title="Add New Patient"
          showPrescriptionOption={true}
        />

        {/* Add Appointment Modal */}
        <AddAppointmentModal
          isOpen={showAddAppointmentModal}
          onClose={() => setShowAddAppointmentModal(false)}
          onAdded={() => {
            // Handle the addition of a new appointment
            loadDashboardData();
            toast({
              title: "Appointment Created",
              description: "New appointment has been scheduled successfully.",
              variant: "default"
            });
          }}
          patients={patients}
          showPatientSelection={true}
          title="New Appointment"
          description="Schedule a new patient appointment"
        />
    </div>
  );
}